import { useMemo, useState, type ReactNode } from "react";

import { useLanguage } from "@/i18n/LanguageContext";

type Props<T> = {
  items: T[];
  initialPageSize?: number;
  children: (pageItems: T[]) => ReactNode;
};

export function useClientPagination<T>(items: T[], initialPageSize = 25) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, pageSize, safePage]);

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize: (n: number) => {
      setPageSize(n);
      setPage(1);
    },
    pages,
    pageItems,
    total: items.length,
    from: items.length ? (safePage - 1) * pageSize + 1 : 0,
    to: Math.min(safePage * pageSize, items.length),
  };
}

export function PaginationBar({
  page,
  pages,
  from,
  to,
  total,
  pageSize,
  onPage,
  onPageSize,
}: {
  page: number;
  pages: number;
  from: number;
  to: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize: (n: number) => void;
}) {
  const { t } = useLanguage();
  if (total === 0) return null;
  return (
    <div className="dn-pagination">
      <p>
        {t.common.showingCount
          .replace("{from}", String(from))
          .replace("{to}", String(to))
          .replace("{total}", String(total))}
      </p>
      <div className="dn-pagination-controls">
        <label>
          {t.common.rowsPerPage}{" "}
          <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="dn-btn dn-btn-ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          {t.common.previousPage}
        </button>
        <span>
          {t.common.pageOf.replace("{page}", String(page)).replace("{pages}", String(pages))}
        </span>
        <button
          type="button"
          className="dn-btn dn-btn-ghost"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          {t.common.nextPage}
        </button>
      </div>
    </div>
  );
}

/** Convenience unused generic wrapper kept for potential future use. */
export function ClientPaginatedList<T>({ items, initialPageSize = 25, children }: Props<T>) {
  const p = useClientPagination(items, initialPageSize);
  return (
    <>
      {children(p.pageItems)}
      <PaginationBar
        page={p.page}
        pages={p.pages}
        from={p.from}
        to={p.to}
        total={p.total}
        pageSize={p.pageSize}
        onPage={p.setPage}
        onPageSize={p.setPageSize}
      />
    </>
  );
}
